// Boostt AI · Brand Tokens v3.0
// Source of truth for all brand values.

export const BRAND = {
  navy:        '#0D2430',
  teal:        '#1B8187',
  light:       '#58C3BE',
  yellow:      '#ECBF2B',
  yellowLight: '#FDF38E',
} as const;

export const SURFACE = {
  light: {
    app:    '#F7FAFB',
    canvas: '#FFFFFF',
    hover:  '#EEF3F5',
    active: '#E4ECF0',
  },
  dark: {
    app:    '#0A1418',
    canvas: '#0F1B22',
    hover:  '#182832',
    active: '#1F3340',
  },
} as const;

export const TEXT = {
  light: {
    primary:    '#0D2430',
    secondary:  '#3A4A52',
    tertiary:   '#6B7B82',
    quaternary: '#A3B0B6',
  },
  dark: {
    primary:    '#E8EEF0',
    secondary:  '#B8C5CA',
    tertiary:   '#7A8B92',
    quaternary: '#4C5A61',
  },
} as const;

export const LINE = {
  light: {
    default: 'rgba(13, 36, 48, 0.10)',
    strong:  'rgba(13, 36, 48, 0.18)',
    accent:  'rgba(27, 129, 135, 0.28)',
  },
  dark: {
    default: 'rgba(232, 238, 240, 0.08)',
    strong:  'rgba(232, 238, 240, 0.16)',
    accent:  'rgba(88, 195, 190, 0.32)',
  },
} as const;

export const ACCENT = {
  light: {
    text:      '#1B8187',
    bg:        'rgba(27, 129, 135, 0.10)',
    bgStrong:  'rgba(27, 129, 135, 0.18)',
    border:    'rgba(27, 129, 135, 0.28)',
  },
  dark: {
    text:      '#1B8187',
    bg:        'rgba(88, 195, 190, 0.12)',
    bgStrong:  'rgba(88, 195, 190, 0.22)',
    border:    'rgba(88, 195, 190, 0.32)',
  },
} as const;

export const STATUS = {
  light: {
    success: { text: '#2D8B5F', bg: 'rgba(45, 139, 95, 0.10)',   border: 'rgba(45, 139, 95, 0.28)' },
    warning: { text: '#B8901E', bg: 'rgba(236, 191, 43, 0.14)',  border: 'rgba(184, 144, 30, 0.28)' },
    danger:  { text: '#C73A3A', bg: 'rgba(199, 58, 58, 0.10)',   border: 'rgba(199, 58, 58, 0.28)' },
  },
  dark: {
    success: { text: '#69D391', bg: 'rgba(105, 211, 145, 0.14)', border: 'rgba(105, 211, 145, 0.32)' },
    warning: { text: '#ECBF2B', bg: 'rgba(236, 191, 43, 0.14)',  border: 'rgba(236, 191, 43, 0.32)' },
    danger:  { text: '#EF6363', bg: 'rgba(239, 99, 99, 0.14)',   border: 'rgba(239, 99, 99, 0.32)' },
  },
} as const;

// `fg` is the WCAG-checked text color for a SOLID `color` fill (e.g. avatar
// initials) — not for the low-opacity `bg` tint, which always pairs with
// `color` itself as the foreground (chart labels, chips, etc).
export const DATA_VIZ = {
  light: [
    { color: '#1B8187', bg: 'rgba(27, 129, 135, 0.10)',   label: 'teal',  fg: '#FFFFFF' },
    { color: '#B8901E', bg: 'rgba(184, 144, 30, 0.10)',   label: 'amber', fg: '#0D2430' },
    { color: '#C45A47', bg: 'rgba(196, 90, 71, 0.10)',    label: 'coral', fg: '#0D2430' },
    { color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.10)',   label: 'plum',  fg: '#0D2430' },
    { color: '#2563EB', bg: 'rgba(37, 99, 235, 0.10)',    label: 'sky',   fg: '#FFFFFF' },
    { color: '#65A30D', bg: 'rgba(101, 163, 13, 0.10)',   label: 'lime',  fg: '#0D2430' },
    { color: '#64748B', bg: 'rgba(100, 116, 139, 0.10)',  label: 'slate', fg: '#FFFFFF' },
    { color: '#A88862', bg: 'rgba(168, 136, 98, 0.10)',   label: 'sand',  fg: '#0D2430' },
  ],
  dark: [
    { color: '#58C3BE', bg: 'rgba(88, 195, 190, 0.18)',   label: 'teal',  fg: '#0D2430' },
    { color: '#ECBF2B', bg: 'rgba(236, 191, 43, 0.18)',   label: 'amber', fg: '#0D2430' },
    { color: '#F08770', bg: 'rgba(240, 135, 112, 0.18)',  label: 'coral', fg: '#0D2430' },
    { color: '#C084FC', bg: 'rgba(192, 132, 252, 0.18)',  label: 'plum',  fg: '#0D2430' },
    { color: '#7BB8E8', bg: 'rgba(123, 184, 232, 0.18)',  label: 'sky',   fg: '#0D2430' },
    { color: '#A3E635', bg: 'rgba(163, 230, 53, 0.18)',   label: 'lime',  fg: '#0D2430' },
    { color: '#94A3B8', bg: 'rgba(148, 163, 184, 0.18)',  label: 'slate', fg: '#0D2430' },
    { color: '#D4B896', bg: 'rgba(212, 184, 150, 0.18)',  label: 'sand',  fg: '#0D2430' },
  ],
} as const;

export const FONT = {
  body:    "'Onest', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  display: "'Instrument Serif', 'Fraunces', Georgia, serif",
  mono:    "'JetBrains Mono', 'SF Mono', Consolas, monospace",
} as const;

export const TYPE_SCALE = {
  display: { size: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, letterSpacing: '-0.02em' },
  h1:      { size: '26px',  weight: 700, letterSpacing: '-0.01em' },
  h2:      { size: '18px',  weight: 700, letterSpacing: '-0.005em' },
  h3:      { size: '16px',  weight: 600 },
  body:    { size: '14px',  lineHeight: 1.5 },
  sm:      { size: '13px' },
  caption: { size: '12px' },
  micro:   { size: '10px',  weight: 700, letterSpacing: '0.06em' },
  metric:  { size: '36px',  lineHeight: 1.1, letterSpacing: '-0.02em' },
} as const;

export const SPACE = {
  1:  4,
  2:  8,
  3:  12,
  4:  16,
  5:  20,
  6:  24,
  7:  28,
  8:  32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
} as const;

export const RADIUS = {
  xs:   4,
  sm:   6,
  md:   10,
  lg:   14,
  xl:   20,
  full: 999,
} as const;

export const MOTION = {
  duration: {
    instant: 60,
    fast:    120,
    base:    220,
    slow:    420,
    glacial: 800,
  },
  easing: {
    default: 'cubic-bezier(0.32, 0.72, 0.36, 1.0)',
    spring:  'cubic-bezier(0.34, 1.56, 0.64, 1.0)',
  },
  staggerInterval: 40,
} as const;

export const Z = {
  tooltip:  50,
  dropdown: 100,
  modal:    1000,
  drawer:   1001,
  popover:  1099,
  palette:  1100,
  toast:    2000,
} as const;

export const LAYOUT = {
  navWidth:          260,
  navWidthCollapsed: 64,
  topbarHeight:      56,
  modalSm:           420,
  modalMd:           560,
  modalLg:           720,
  drawerWidth:       440,
  toastMaxWidth:     400,
  dropdownMinWidth:  200,
  paletteMaxWidth:   560,
} as const;

export const BREAKPOINTS = {
  mobile:  640,
  tablet:  1024,
  desktop: 1025,
} as const;

export const DENSITY = {
  compact:     0.75,
  default:     1.0,
  comfortable: 1.25,
} as const;

export const SHADOW = {
  light: {
    1: '0 8px 24px rgba(13, 36, 48, 0.12)',
    2: '0 12px 32px rgba(13, 36, 48, 0.18), 0 2px 8px rgba(13, 36, 48, 0.08)',
    3: '0 24px 60px rgba(13, 36, 48, 0.28), 0 4px 12px rgba(13, 36, 48, 0.12)',
  },
  dark: {
    1: '0 8px 24px rgba(0, 0, 0, 0.25)',
    2: '0 12px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.25)',
    3: '0 24px 60px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4)',
  },
} as const;

// Each solid color is paired with a WCAG-checked `fg` text color — several of
// these (yellow, the lighter purples/blues) fail contrast against the
// `common.white` text that used to be hardcoded at every call site.
export const PROJECT_AVATAR_COLORS = [
  { bg: "#4A7C9E", fg: "#0D2430" },
  { bg: "#5BAD8A", fg: "#0D2430" },
  { bg: "#B07ED4", fg: "#0D2430" },
  { bg: "#E8705A", fg: "#0D2430" },
  { bg: "#ECBF2B", fg: "#0D2430" },
  { bg: "#6495ED", fg: "#0D2430" },
] as const;

// `fg` is checked against the gradient's midpoint (135deg over a roughly square
// swatch puts centered initials near the average of the two stops) — several of
// these fade into a pale end where the old hardcoded `common.white` failed.
export const GRADIENT = {
  avatar: [
    { css: "linear-gradient(135deg, #58C3BE, #8ad8d5)", fg: "#0D2430" },
    { css: "linear-gradient(135deg, #f5a623, #f7c66a)", fg: "#0D2430" },
    { css: "linear-gradient(135deg, #7b61ff, #a89af0)", fg: "#0D2430" },
    { css: "linear-gradient(135deg, #e85d75, #f29ba8)", fg: "#0D2430" },
    { css: "linear-gradient(135deg, #34c97a, #7ae0aa)", fg: "#0D2430" },
  ],
  project: [
    { css: "linear-gradient(135deg, #178086, #57c2be)", fg: "#0D2430" },
    { css: "linear-gradient(135deg, #ecbf2d, #f4db7d)", fg: "#0D2430" },
    { css: "linear-gradient(135deg, #7c3aed, #a78bfa)", fg: "#0D2430" },
  ],
} as const;

